import {
  Blocks, Dumbbell, Flag, Footprints, Mountain, Star, Swords, Target, Timer,
  Users, Waves, Weight, type LucideIcon,
} from 'lucide-react';

/**
 * The twelve event types, and what each one opens into.
 *
 * FORMATS ARE NOT INVENTED FOR THE TWO LIVE TYPES. Endurance and Functional
 * fitness list the templates that actually ship in apps/web/lib/wizard/templates.ts
 * (Fun Run, Social Run, Running Race, Trail Run, Triathlon, Obstacle Course Race;
 * HYROX, TRYKA, Deadly Dozen, DEKA, CrossFit, Hybrid). A marketing page that
 * names a format an organiser then cannot find in the wizard is worse than one
 * that names fewer, so if you add a name here, add the template first.
 *
 * The ten `live: false` types are a roadmap, and every surface that shows them
 * says "Coming soon" in the same breath. That is the honest way to show them:
 * the tiles are how an organiser sees whether their sport is coming, and hiding
 * them entirely would answer that question wrongly too.
 *
 * `more` is DERIVED, never written down. It was hand-typed at first and drifted
 * from the list within a day; a "+5 more" that opens onto three items is the
 * kind of small lie a careful reader notices.
 *
 * ICONS: lucide has no horse, so Equestrian takes Flag (the fences) and Racket
 * takes Target. If a better mark turns up, these are the two to revisit.
 */

export type EventType = {
  name: string;
  icon: LucideIcon;
  live: boolean;
  /** The one line that says what changes for this sport. */
  engine: string;
  /** How the engine actually runs, shown when the tile is opened. */
  how: string[];
  /** Every format. The tile previews the first few and derives the rest. */
  formats: string[];
  img: string;
};

/**
 * Formats shown as chips on the tile itself; the rest become "+N more".
 * Three, because the chip row is a SINGLE line on a 300px tile and a fourth
 * chip wraps, which knocks the count line out of alignment with its neighbours.
 */
export const CHIPS_ON_TILE = 3;

export const EVENT_TYPES: EventType[] = [
  {
    name: 'Endurance',
    icon: Footprints,
    live: true,
    engine: 'Waves and corrals, scored on finish time',
    how: [
      'Waves and corrals built from entries, or picked by athletes at checkout up to a capacity you set.',
      'Finish times entered on the day, with results published to athletes the moment you release them.',
      'Age and gender guidance shown at entry, with anything outside it flagged for you.',
    ],
    formats: ['Running race', 'Trail run', 'Triathlon', 'Obstacle course race', 'Fun run', 'Social run'],
    img: '/website/uploads/jens-freudenau-iZgfvYwo69Q-unsplash.jpg',
  },
  {
    name: 'Functional fitness',
    icon: Dumbbell,
    live: true,
    engine: 'Heats and lanes, scored on judged reps',
    how: [
      'Heats and lanes generated from divisions, with singles, doubles and relay teams kept together.',
      'Judged from the floor on a phone, so scores land without a paper sheet in between.',
      'Station and rep schemes carried in from the branded rulebooks rather than typed out each time.',
    ],
    formats: ['HYROX', 'TRYKA', 'Deadly Dozen', 'DEKA', 'CrossFit', 'Hybrid race'],
    img: '/website/uploads/grace-anne-bobadilla-z2ltNyVDsNY-unsplash.jpg',
  },
  {
    name: 'Team sports',
    icon: Users,
    live: false,
    engine: 'Pools and knockouts, scored on results',
    how: [
      'Pools drawn from entered squads, then a knockout bracket built from how the pools finish.',
      'Fixtures, pitches and kick-off times scheduled together so no team is double-booked.',
      'Results entered per fixture, with tables standing themselves up as they come in.',
    ],
    formats: ['Football', 'Rugby', 'GAA', 'Hockey', 'Basketball', 'Netball', 'Volleyball', 'Ultimate'],
    img: '/website/uploads/no-direction-vEAoVhbXth8-unsplash.jpg',
  },
  {
    name: 'Racket and club',
    icon: Target,
    live: false,
    engine: 'Seeded draws, scored on games and sets',
    how: [
      'Seeded draws by grade or rating, with byes placed properly rather than left to chance.',
      'Court allocation and rotation across the day, so nobody waits three hours for a ten-minute match.',
      'Scores by game and set, feeding straight into the next round of the draw.',
    ],
    formats: ['Padel', 'Tennis', 'Pickleball', 'Badminton', 'Squash', 'Table Tennis', 'Golf'],
    img: '/website/uploads/abror-alarice-xAUuSl7oiY4-unsplash.jpg',
  },
  {
    name: 'Strength',
    icon: Weight,
    live: false,
    engine: 'Flights and attempts, scored on best lift',
    how: [
      'Flights built from weight class and opening attempt, then re-ordered as attempts are declared.',
      'Three attempts per lift, with the bar loaded from the running order rather than shouted across the room.',
      'Totals and coefficients scored automatically, including masters and junior categories.',
    ],
    formats: ['Powerlifting', 'Weightlifting', 'Strongman', 'Bench Press'],
    img: '/website/shots/sport-strength.jpg',
  },
  {
    name: 'Combat',
    icon: Swords,
    live: false,
    engine: 'Brackets by weight, scored bout by bout',
    how: [
      'Brackets cut by weight, belt and age, with weigh-in results feeding the draw.',
      'Mat and ring scheduling across the day, so corners know where to be and when.',
      'Bout-by-bout results advancing fighters without anyone redrawing a bracket by hand.',
    ],
    formats: ['BJJ', 'Judo', 'Boxing', 'MMA', 'Kickboxing', 'Wrestling'],
    img: '/website/uploads/mahmoud-gad-DcRNYqiRRJU-unsplash.jpg',
  },
  {
    name: 'Judged',
    icon: Star,
    live: false,
    engine: 'Rounds and panels, scored on placings',
    how: [
      'Running order by level and age, with routines timed into the session.',
      'Panel scoring on a phone, high and low dropped, tie-breaks resolved by rule.',
      'Placings published per apparatus and overall as each round closes.',
    ],
    formats: ['Gymnastics', 'Dance', 'Cheer', 'Figure Skating', 'Aesthetics'],
    img: '/website/shots/sport-judged.jpg',
  },
  {
    name: 'Track and field',
    icon: Timer,
    live: false,
    engine: 'Heats, lanes and attempts, scored on marks',
    how: [
      'Heats seeded on entry times, with lanes drawn to the usual seeding pattern.',
      'Field events run on attempts and heights, with the bar and the board tracked together.',
      'Marks converted to points for combined events and team scoring.',
    ],
    formats: ['Track Meet', 'Field Events', 'Cross Country', 'Road Relay', 'Combined Events', 'Masters Meet'],
    img: '/website/uploads/selma-da-silva-1O33RAdh5h4-unsplash.jpg',
  },
  {
    name: 'Aquatics',
    icon: Waves,
    live: false,
    engine: 'Seeded lanes and relay legs, scored on time',
    how: [
      'Lanes seeded on entry times, slowest heats first, exactly as a gala expects.',
      'Relay legs held together as a team while still timing each swimmer.',
      'Session and event ordering across a full programme, with warm-up windows built in.',
    ],
    formats: ['Pool Gala', 'Masters Swim', 'Relay Meet', 'Open Water', 'Aquathlon'],
    img: '/website/shots/sport-aquatics.jpg',
  },
  {
    name: 'Climbing',
    icon: Mountain,
    live: false,
    engine: 'Rounds and problems, scored on tops and zones',
    how: [
      'Qualification, semi and final rounds, with athletes carried through on rank.',
      'Tops, zones and attempts counted per problem, and countback applied on ties.',
      'Rotation across problems so the wall is used evenly through the session.',
    ],
    formats: ['Bouldering', 'Lead Climbing', 'Speed', 'Youth Series'],
    img: '/website/uploads/jeff-ochoa-FcntrYZC654-unsplash.jpg',
  },
  {
    name: 'Equestrian',
    icon: Flag,
    live: false,
    engine: 'Classes and rounds, scored on faults and time',
    how: [
      'Classes by height and grade, with the order of go drawn and published.',
      'Faults and time per round, and a jump-off scored separately against the clock.',
      'Horse and rider tracked as a pair, including riders declaring more than one horse.',
    ],
    formats: ['Show Jumping', 'Dressage', 'Eventing', 'Showing', 'Pony Club'],
    img: '/website/shots/sport-equestrian.jpg',
  },
  {
    name: 'Build your own',
    icon: Blocks,
    live: false,
    engine: 'Your own format, with scoring to match',
    how: [
      'Rounds, stations and scoring defined by you, rather than bent into somebody else’s template.',
      'Points, time or reps, with progression rules from one round to the next.',
      'Everything in the core still applies: tickets, teams, waivers, comms, payouts.',
    ],
    formats: ['Custom rounds', 'Custom heats', 'Custom points', 'Custom progression'],
    img: '/website/uploads/felix-yu-ZlwCocz11D8-unsplash.jpg',
  },
];
